/**
 * 账号：用户与计费列表（Alice 版式，抽屉里管封禁与调账），账号详情在 AccountDetail.tsx（/accounts/:id）。
 *
 * 背景：2026-11-03 取消组织，Key、钱包、用量直接挂账号。账号分 kind：user（有登录入口）与 service（服务账号，
 *   例如 Yeou 游戏服务端，没有登录入口，只由运营在这里管理），列表用类型筛选与徽标把两类分开。
 * 设计意图：
 *   - 搜索词放在地址栏 ?q=，刷新和回退都保留结果。
 *   - 类型筛选在服务端做（kind 参数），翻页不丢条件；服务账号在名称旁显示「服务账号」徽标。
 * 关键约束：按钮显隐只是体验，权限以后端为准（identity/routes_admin.py）。
 */
import { IconSearch } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { usersApi, type UserKind, type UserRow, type UserSort } from '@ap/api-client/admin-users'
import { A } from '@ap/i18n/admin'
import { navigate, useRoute } from '@ap/ui'
import { useAdmin } from '../session.tsx'
import { OpsPage, Pager } from '../alice/kit.tsx'
import { PILL, Pill, ToolSelect } from '../alice/kit-runtime2.tsx'
import { Check, compact, useInt, useQuery, useU } from '../alice/kit-users.tsx'
import { BatchAdjustModal } from '../alice/g2/BatchAdjustModal.tsx'
import { UserDrawer } from '../alice/g2/UserDrawer.tsx'

/**
 * 用户与计费（Alice 页面 users 的复刻）：用户资产列表加右侧详情抽屉。
 *
 * 背景：Alice 的「用户」就是账号；本仓余额、Key、用量也直接挂账号。
 *   列表数据来自控制面 /control/admin/users：账号目录副本（不含邮箱与姓名）与钱包、Key、用量汇总在本库里排序分页，
 *   邮箱与昵称按页向账号服务补取。账号详情（Key、钱包、流水、审计）从抽屉进入。
 * 设计意图：
 *   - 搜索框输入后 350ms 自动查询，回车立即查询；排序、精确 ID、只看充过钱的都在服务端做，翻页不丢条件。
 *   - 抽屉里的写操作成功后只刷新列表当前页（onChanged），不丢选中行与页码。
 *   - 支持 ?user=<账号 ID> 直接打开某个用户的抽屉，别的页面（账号详情、订单）可以链过来。
 * 关键约束：写操作（封禁、调账等）要 admin，按钮对 support 隐藏，后端为准；Alice 的测试账号筛选、订单号后缀搜索在本仓没有对应物。
 */
function usersSortArrow(active: boolean, order: string) {
  return <span className="th-arrow">{active ? (order === 'asc' ? '▲' : '▼') : ''}</span>
}

const USERS_PAGE = 50

export function Accounts() {
  const { t, G, err, num } = useU()
  const U = G.users
  const int = useInt()
  const { isAdmin } = useAdmin()
  const { query } = useRoute()
  const wanted = query.get('user') ?? ''
  const [draft, setDraft] = useState(query.get('q') ?? '')
  const [q, setQ] = useState(query.get('q') ?? '')
  const [exact, setExact] = useState(false)
  const [paidOnly, setPaidOnly] = useState(false)
  const [kind, setKind] = useState<'' | UserKind>('')
  const [sort, setSort] = useState<UserSort>('balance')
  const [order, setOrder] = useState<'asc' | 'desc'>('desc')
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<UserRow | null>(null)
  const [batch, setBatch] = useState(false)

  // 输入防抖：停 350ms 再查，回车立即查
  useEffect(() => {
    const id = window.setTimeout(() => {
      setQ(draft.trim())
      setOffset(0)
    }, 350)
    return () => window.clearTimeout(id)
  }, [draft])

  const list = useQuery(
    () => usersApi.users({ q, exact_id: exact, paid_only: paidOnly, kind: kind || undefined, sort, order, limit: USERS_PAGE, offset }),
    [q, exact, paidOnly, kind, sort, order, offset],
  )

  // ?user=<ID>：按精确 ID 取一行并打开抽屉
  useEffect(() => {
    if (!wanted || selected?.account_id === wanted) return
    let alive = true
    usersApi
      .users({ q: wanted, exact_id: true, limit: 1 })
      .then((r) => alive && r.users[0] && setSelected(r.users[0]))
      .catch((e) => console.error('[users] 打开指定用户失败', e))
    return () => {
      alive = false
    }
  }, [wanted]) // eslint-disable-line react-hooks/exhaustive-deps

  const closeDrawer = () => {
    setSelected(null)
    if (wanted) navigate('/accounts', { replace: true })
  }
  const toggleSort = (key: UserSort) => {
    if (sort === key) setOrder(order === 'desc' ? 'asc' : 'desc')
    else {
      setSort(key)
      setOrder('desc')
    }
    setOffset(0)
  }
  const th = (key: UserSort, label: string) => (
    <th className={`th-sort${sort === key ? ' is-active' : ''}`} onClick={() => toggleSort(key)}>
      {label}
      {usersSortArrow(sort === key, order)}
    </th>
  )

  const rows = list.data?.users ?? []
  return (
    <div id="panel-users">
      <OpsPage
        eyebrow={t(U.eyebrow)}
        title={t(U.title)}
        desc={t(U.desc)}
        chip={t(U.chip)}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isAdmin && (
              <button className="btn btn-primary btn-sm" onClick={() => setBatch(true)}>
                {t(U.batchBtn)}
              </button>
            )}
          </div>
        }
      >
        <div className="users-layout">
          <div className="users-list-pane">
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-medium)' }}>
              <div className="search-wrap">
                <IconSearch size={14} />
                <input
                  className="search-input"
                  type="text"
                  style={{ width: '100%' }}
                  placeholder={t(U.searchPh)}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                      setQ(draft.trim())
                      setOffset(0)
                    }
                  }}
                />
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <Check
                  checked={exact}
                  onChange={(v) => {
                    setExact(v)
                    setOffset(0)
                  }}
                  hint={t(U.exactIdHint)}
                >
                  {t(U.exactId)}
                </Check>
                <Check
                  checked={paidOnly}
                  onChange={(v) => {
                    setPaidOnly(v)
                    setOffset(0)
                  }}
                >
                  {t(U.paidOnly)}
                </Check>
                <ToolSelect
                  aria-label={t(A.accounts.kind)}
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value as '' | UserKind)
                    setOffset(0)
                  }}
                  options={[
                    { value: '', label: t(A.accounts.kindAll) },
                    { value: 'user', label: t(A.accounts.kindUser) },
                    { value: 'service', label: t(A.accounts.kindService) },
                  ]}
                  style={{ padding: '3px 26px 3px 8px', fontSize: 12 }}
                />
              </div>
              <div className="u2-note" style={{ marginTop: 6 }}>
                {t(U.keyHint, { ex: 'a3Fk' })}
              </div>
            </div>
            <div className="users-list-inner">
              <table>
                <thead>
                  <tr>
                    <th>{t(U.colUser)}</th>
                    {th('balance', t(U.colBalance))}
                    {th('cost', t(U.colCost))}
                    {th('requests', t(U.colRequests))}
                    {th('tokens', t(U.colTokens))}
                    {th('keys', t(U.colKeys))}
                  </tr>
                </thead>
                <tbody id="usersBody">
                  {list.error != null && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--error-text)' }}>
                        {t(G.common.loadFailed, { msg: err(list.error) })}
                      </td>
                    </tr>
                  )}
                  {list.error == null && list.data && rows.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)' }}>
                        {t(U.empty)}
                      </td>
                    </tr>
                  )}
                  {list.loading && !list.data && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)' }}>
                        {t(G.common.loading)}
                      </td>
                    </tr>
                  )}
                  {rows.map((u) => (
                    <tr
                      key={u.account_id}
                      className={selected?.account_id === u.account_id ? 'is-selected' : undefined}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelected(u)}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600 }}>{u.display_name || u.email || t(U.noName)}</span>
                          {u.kind === 'service' && <Pill tone={PILL.amber}>{t(A.accounts.kindService)}</Pill>}
                          {u.is_banned && <Pill tone={PILL.red}>{t(U.badgeBanned)}</Pill>}
                          {u.is_internal && <Pill tone={PILL.blue}>{t(U.badgeInternal)}</Pill>}
                          {u.geo_exempt && <Pill tone={PILL.purple}>{t(U.badgeGeo)}</Pill>}
                          {u.paid_units > 0 && <Pill tone={PILL.green}>{t(U.badgePaid)}</Pill>}
                        </div>
                        <div className="mono" style={{ color: 'var(--text-tertiary)' }}>
                          {u.email}
                        </div>
                      </td>
                      <td style={{ color: u.balance_units < 0 ? 'var(--error-text)' : undefined }}>{num(u.balance_units)}</td>
                      <td>{num(u.total_cost_units)}</td>
                      <td>{int(u.total_requests)}</td>
                      <td>{compact(u.total_tokens)}</td>
                      <td>{u.key_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span>{list.data ? t(U.total, { n: list.data.total }) : ''}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Pager offset={offset} limit={USERS_PAGE} total={list.data?.total ?? 0} onChange={setOffset} />
              </span>
            </div>
          </div>
        </div>
      </OpsPage>
      <UserDrawer user={selected} onClose={closeDrawer} onChanged={list.reload} />
      <BatchAdjustModal open={batch} onClose={() => setBatch(false)} onDone={list.reload} />
    </div>
  )
}
